import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, ShieldCheck, Lock } from "lucide-react";

export default function Roles() {
  const [catalog, setCatalog] = useState({ modules: [], all_keys: [] });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [c, r] = await Promise.all([
      api.get("/admin/permissions/catalog"),
      api.get("/admin/roles"),
    ]);
    setCatalog(c.data);
    setRoles(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Roles & Permissions</h1>
          <p className="text-sm text-slate-500 mt-1">
            Control what each role can do, module-wise and action-wise. {roles.length} roles · {catalog.all_keys.length} permission keys.
          </p>
        </div>
        <RoleDialog mode="create" catalog={catalog} onDone={load} />
      </div>

      {loading && <div className="text-sm text-slate-500">Loading...</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {roles.map((r) => (
          <Card key={r.id} data-testid={`role-card-${r.key}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {r.built_in ? <Lock size={14} className="text-slate-400" /> : <ShieldCheck size={14} className="text-emerald-600" />}
                    <span className="font-heading font-semibold text-slate-900">{r.name}</span>
                    <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r.key}</span>
                    {r.built_in && <span className="text-[10px] uppercase tracking-wider text-slate-500">built-in</span>}
                    {!r.active && <span className="text-[10px] uppercase tracking-wider text-rose-600">inactive</span>}
                  </div>
                  {r.description && <p className="text-xs text-slate-500 mt-1">{r.description}</p>}
                  <p className="text-xs text-slate-700 mt-2">
                    <span className="font-semibold">{(r.permissions || []).includes("*") ? "All permissions (*)" : `${(r.permissions || []).length} permissions`}</span>
                  </p>
                </div>
                <RoleDialog mode="edit" role={r} catalog={catalog} onDone={load} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}


function RoleDialog({ mode, role, catalog, onDone }) {
  const isEdit = mode === "edit";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(role?.name || "");
  const [key, setKey] = useState(role?.key || "");
  const [desc, setDesc] = useState(role?.description || "");
  const [active, setActive] = useState(role?.active ?? true);
  const [perms, setPerms] = useState(new Set(role?.permissions || []));

  useEffect(() => {
    if (open) {
      setName(role?.name || "");
      setKey(role?.key || "");
      setDesc(role?.description || "");
      setActive(role?.active ?? true);
      setPerms(new Set(role?.permissions || []));
    }
  }, [open, role]);

  const togglePerm = (p) => {
    const n = new Set(perms);
    if (n.has(p)) n.delete(p); else n.add(p);
    setPerms(n);
  };
  const allOn = (modKey) => {
    const moduleDef = catalog.modules.find((m) => m.key === modKey);
    if (!moduleDef) return false;
    return moduleDef.actions.every((a) => perms.has(`${modKey}.${a.key}`));
  };
  const toggleModuleAll = (modKey) => {
    const moduleDef = catalog.modules.find((m) => m.key === modKey);
    const n = new Set(perms);
    const on = allOn(modKey);
    for (const a of moduleDef.actions) {
      const p = `${modKey}.${a.key}`;
      if (on) n.delete(p); else n.add(p);
    }
    setPerms(n);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!isEdit && !key.trim() && !name.trim()) { toast.error("Key is required"); return; }
    const payload = {
      key: key.trim() || undefined,
      name: name.trim(),
      description: desc.trim() || null,
      permissions: Array.from(perms),
      active,
    };
    try {
      if (isEdit) await api.put(`/admin/roles/${role.id}`, payload);
      else await api.post("/admin/roles", payload);
      toast.success(isEdit ? "Role updated" : "Role created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="outline" size="sm" data-testid={`edit-role-${role.key}`}>Edit</Button>
          : <Button className="bg-slate-900 text-white" data-testid="new-role-btn"><Plus size={14} className="mr-1" /> New Role</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit role · ${role.name}` : "Create custom role"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={isEdit && role?.built_in && false} data-testid="role-name-input" />
            </div>
            <div>
              <Label>Key {isEdit && <span className="text-[10px] text-slate-400">(immutable for built-ins)</span>}</Label>
              <Input value={key} onChange={(e) => setKey(e.target.value)} disabled={isEdit && role?.built_in} placeholder="auto from name" data-testid="role-key-input" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
          {(!isEdit || !role?.built_in) && (
            <div className="flex items-center gap-2">
              <Checkbox checked={active} onCheckedChange={(v) => setActive(!!v)} data-testid="role-active-checkbox" />
              <Label>Active</Label>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Permissions</Label>
              {isEdit && role?.key === "admin" && (
                <span className="text-[11px] text-amber-700">Admin always has all permissions (*)</span>
              )}
            </div>
            <div className="space-y-3">
              {catalog.modules.map((m) => (
                <div key={m.key} className="border border-slate-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm text-slate-900 capitalize">{m.key.replace(/_/g, " ")}</div>
                    <button type="button" className="text-xs text-slate-500 hover:text-slate-900"
                            onClick={() => toggleModuleAll(m.key)} data-testid={`perm-toggle-all-${m.key}`}>
                      {allOn(m.key) ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {m.actions.map((a) => {
                      const p = `${m.key}.${a.key}`;
                      return (
                        <label key={p} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <Checkbox checked={perms.has(p) || perms.has("*") || perms.has(`${m.key}.*`)}
                                    onCheckedChange={() => togglePerm(p)}
                                    data-testid={`perm-${p}`} />
                          <span>{a.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-900 text-white" data-testid="role-save-btn">
            {isEdit ? "Save" : "Create role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
